import type { Translate } from "@/lib/i18n/translate";
/**
 * Data + row renderers for the Inbox module (split from `module-inbox.tsx` to
 * keep each file under the line budget). All copy / px / colors are ported 1:1
 * from the prototype (`Hausverwaltung Hero.dc.html`, `isInbox` blocks).
 */
import { Building2, Inbox } from "lucide-react";
import type { ReactNode } from "react";

import { H } from "../../hero-tokens";
import { HeroClickPulseDot } from "./hero-click-pulse-dot";
import type { HeroVorgangConversationId } from "./module-inbox-vorgang-data";

export type InboxBoardId =
  | "allgemein"
  | "schaeden"
  | "schluesselbestellungen"
  | "mietinteressenten"
  | "rechnungen"
  | "wartungen"
  | "zaehlerstaende";

export type InboxSidebarSelection =
  | { readonly kind: "mine" }
  | { readonly kind: "all" }
  | { readonly kind: "unassigned" }
  | { readonly kind: "board"; readonly boardId: InboxBoardId };

export type InboxBoardMeta = {
  readonly id: InboxBoardId;
  readonly label: string;
};

export function inboxBoards(t: Translate): readonly InboxBoardMeta[] {
  return [
    { id: "allgemein", label: t("allgemein") },
    { id: "schaeden", label: t("schaeden") },
    { id: "schluesselbestellungen", label: t("schluesselbestellungen") },
    { id: "mietinteressenten", label: t("mietinteressenten") },
    { id: "rechnungen", label: t("rechnungen") },
    { id: "wartungen", label: t("wartungen") },
    { id: "zaehlerstaende", label: t("zaehlerstaende") },
  ];
}

/** One conversation row in the inbox list. */
export type InboxConversation = {
  readonly id: HeroVorgangConversationId;
  readonly initials: string;
  readonly avBg: string;
  readonly avFg: string;
  readonly name: string;
  readonly email: string | null;
  readonly subject: string;
  readonly snippet: string;
  readonly subjColor: string;
  readonly hasObject: boolean;
  readonly object: string;
  readonly category: string;
  readonly boardId: InboxBoardId;
  readonly time: string;
  readonly assignInit: string;
  readonly assignBg: string;
  /** Shown in «Meine Vorgänge» (assigned to the viewing Mitarbeiter). */
  readonly assignedToViewer: boolean;
  /** Shown in «Unzugewiesen». */
  readonly isUnassigned: boolean;
};

/** Prototype `conversations` array (lines 2641–2645) — verbatim. */
export function inboxConversations(t: Translate): readonly InboxConversation[] {
  return [
    {
      id: "sabine-krueger",
      initials: "SK",
      avBg: "#EEE9F7",
      avFg: "#6D5BA6",
      name: t("sabineKrueger"),
      email: "sabine.krueger@…",
      subject: t("heizungswartungAbstimmen"),
      snippet: t("halloFrauKruegerVielen"),
      subjColor: "#0B0F19",
      hasObject: false,
      object: "",
      category: t("wartungen"),
      boardId: "wartungen",
      time: t("k3Std"),
      assignInit: "NH",
      assignBg: "#0B0F19",
      assignedToViewer: true,
      isUnassigned: false,
    },
    {
      id: "michael-bauer",
      initials: "MB",
      avBg: "#E7EEF8",
      avFg: "#3F6CB0",
      name: t("michaelBauer"),
      email: null,
      subject: t("fensterLaesstNichtSchliessen"),
      snippet: t("gutenTagSchlafzimmerKlemmt"),
      subjColor: H.accent,
      hasObject: true,
      object: "Ahornweg 5",
      category: t("schaeden"),
      boardId: "schaeden",
      time: t("k10Min"),
      assignInit: "NH",
      assignBg: "#0B0F19",
      assignedToViewer: true,
      isUnassigned: false,
    },
    {
      id: "klaus-richter",
      initials: "KR",
      avBg: "#F6E9EE",
      avFg: "#B05B79",
      name: t("klausRichter"),
      email: "klaus.richter@…",
      subject: t("wasserschadenKueche"),
      snippet: t("halloHerrRichterVielen"),
      subjColor: "#0B0F19",
      hasObject: true,
      object: "Birkenallee 12",
      category: t("schaeden"),
      boardId: "schaeden",
      time: t("k14Juni"),
      assignInit: "TB",
      assignBg: "#0B0F19",
      assignedToViewer: false,
      isUnassigned: false,
    },
  ];
}

export function filterInboxConversations(
  t: Translate,
  selection: InboxSidebarSelection,
): readonly InboxConversation[] {
  if (selection.kind === "mine") {
    return inboxConversations(t).filter((c) => c.assignedToViewer);
  }
  if (selection.kind === "unassigned") {
    return inboxConversations(t).filter((c) => c.isUnassigned);
  }
  if (selection.kind === "board") {
    return inboxConversations(t).filter((c) => c.boardId === selection.boardId);
  }
  return inboxConversations(t);
}

export function countInboxBoardConversations(
  t: Translate,
  boardId: InboxBoardId,
): number {
  return inboxConversations(t).filter((c) => c.boardId === boardId).length;
}

export function getInboxSelectionTitle(
  t: Translate,
  selection: InboxSidebarSelection,
): string {
  if (selection.kind === "mine") return t("meineKonversationen");
  if (selection.kind === "all") return t("alleKonversationen");
  if (selection.kind === "unassigned") return t("unzugewiesen");
  const board: InboxBoardMeta | undefined = inboxBoards(t).find(
    (b) => b.id === selection.boardId,
  );
  return board?.label ?? t("inboxThema");
}

export function inboxMineCount(t: Translate): number {
  return inboxConversations(t).filter((c) => c.assignedToViewer).length;
}

export function inboxAllCount(t: Translate): number {
  return inboxConversations(t).length;
}

export function inboxUnassignedCount(t: Translate): number {
  return inboxConversations(t).filter((c) => c.isUnassigned).length;
}

/** Renders a single conversation row (prototype `sc-for` body, lines 634–656). */
export function InboxConversationRow(props: {
  readonly conversation: InboxConversation;
  readonly onClick?: () => void;
  readonly showClickPulse?: boolean;
}): ReactNode {
  const c = props.conversation;
  return (
    <div
      role={props.onClick !== undefined ? "button" : undefined}
      tabIndex={props.onClick !== undefined ? 0 : undefined}
      onClick={props.onClick}
      onKeyDown={
        props.onClick !== undefined
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                props.onClick?.();
              }
            }
          : undefined
      }
      className={
        [
          props.onClick !== undefined ? "mkhero-inbox-row" : undefined,
          props.showClickPulse === true
            ? "mkhero-inbox-row-hint-target"
            : undefined,
        ]
          .filter(Boolean)
          .join(" ") || undefined
      }
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 13,
        padding: "13px 6px",
        borderBottom: "1px solid #F1F2F4",
        cursor: props.onClick !== undefined ? "pointer" : "default",
      }}
    >
      <span
        onClick={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        style={{
          width: 16,
          height: 16,
          borderRadius: 5,
          border: "1.5px solid #D7DAE0",
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      />
      <span style={{ position: "relative", flexShrink: 0 }}>
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: c.avBg,
            color: c.avFg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: H.sans,
          }}
        >
          {c.initials}
        </span>
        <span
          style={{
            position: "absolute",
            right: -1,
            bottom: -1,
            width: 13,
            height: 13,
            borderRadius: "50%",
            background: "#3A9E6B",
            border: "2px solid #fff",
          }}
        />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#0B0F19" }}>
            {c.name}
          </span>
          {props.showClickPulse === true ? (
            <HeroClickPulseDot placement="inline" />
          ) : null}
          {c.email !== null ? (
            <span
              style={{
                fontSize: 12,
                color: "rgba(11,15,25,0.38)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {c.email}
            </span>
          ) : null}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            marginTop: 3,
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: c.subjColor,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {c.subject}
          </span>
          <span
            style={{
              fontSize: 13,
              color: "rgba(11,15,25,0.45)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {c.snippet}
          </span>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            border: "1px solid #EBEDF0",
            borderRadius: 7,
            fontSize: 12,
            color: "rgba(11,15,25,0.6)",
          }}
        >
          <Inbox width={12} height={12} strokeWidth={1.9} />
          {c.category}
        </span>
        {c.hasObject ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              border: "1px solid #EBEDF0",
              borderRadius: 7,
              fontSize: 12,
              color: "rgba(11,15,25,0.6)",
            }}
          >
            <Building2 width={12} height={12} strokeWidth={1.9} />
            {c.object}
          </span>
        ) : null}
        <span
          style={{
            fontSize: 12,
            color: "rgba(11,15,25,0.4)",
            minWidth: 54,
            textAlign: "right",
          }}
        >
          {c.time}
        </span>
        <span
          style={{
            width: 27,
            height: 27,
            borderRadius: "50%",
            background: c.assignBg,
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 600,
            fontFamily: H.sans,
          }}
        >
          {c.assignInit}
        </span>
      </div>
    </div>
  );
}
